import React from "react";
import { motion } from "framer-motion";
import { Col, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import moment from "moment";
import { ageCalculator } from "helpers/utils";
import { useLocation } from "react-router-dom";

const PreauthPayerResponse = () => {
  const { viewPreauthorizationInfo } = useSelector(
    (state) => state.preauthorization
  );

  const preauthResponse = viewPreauthorizationInfo?.payer_response;

  // Compute status value and style
  const getStatusInfo = () => {
    let statusValue = "";
    let statusStyle = { backgroundColor: "#f8f9fa", color: "#000" };

    if (viewPreauthorizationInfo?.preauth_status) {
      statusValue = viewPreauthorizationInfo.preauth_status.toLowerCase();
    }

    const statusStyles = {
      error: {
        backgroundColor: "#fde6d8",
        color: "#ac5a2b",
        icon: "exclamation",
      },
      rejected: { backgroundColor: "#fee2e2", color: "#dc2626" },
      noresp: { backgroundColor: "#e5e7eb", color: "#374151" },
      approved: { backgroundColor: "#dcfce7", color: "#16a34a" },
      complete: { backgroundColor: "#dcfce7", color: "#16a34a" },
      cancelled: { backgroundColor: "#fce7f6", color: "#db2777" },
      pended: { backgroundColor: "#dbeafe", color: "#2563eb" },
      queued: { backgroundColor: "#fef9c3", color: "#ca8a04" },
      partial: { backgroundColor: "#cffafe", color: "#0891b2" },
      adjudication: { backgroundColor: "#ede9fe", color: "#7c3aed" },
      "not-required": { backgroundColor: "#e0f2fe", color: "#0284c7" },
      unknown: { backgroundColor: "#f8f9fa", color: "#000" }, // Fallback for unknown status
    };

    if (statusValue && statusStyles[statusValue]) {
      statusStyle = statusStyles[statusValue];
    }

    return { statusValue, statusStyle };
  };

  const { statusValue, statusStyle } = getStatusInfo();

  const isAdvanced = viewPreauthorizationInfo?.preauthadv_id;
  const relatedID = preauthResponse?.related;
  const isTag = preauthResponse?.tag;

  const relatedDisplay = relation?.find(
    (item) => item?.rltdrlshp_code === preauthResponse?.relatedrelationship
  )?.rltdrlshp_name;

  const advancedPatient = viewPreauthorizationInfo?.provider_request?.patient;

  const itemDetails = [
    {
      id: 1,
      title: "Response Date and Time",
      value: preauthResponse?.response_datetime
        ? moment(preauthResponse.response_datetime).format(
          "DD-MM-YYYY hh:mm:ss A"
        )
        : "",
    },
    {
      id: 2,
      title: "PreAuth Reference Number",
      value: preauthResponse?.preauth_ref ?? "",
    },

    // Advanced row – only added when isAdvanced is true
    ...(isAdvanced
      ? [
        {
          id: 3,
          title: "Advanced",
          value: "Advanced Preauthorization",
        },
      ]
      : []),

    {
      id: 4,
      title: "Approval Identifier",
      value: viewPreauthorizationInfo?.preauthadv_id || "", // intentionally empty → will be filtered out
      advanced: true,
    },
    {
      id: 5,
      title: "Pre-Authorization Type",
      value: preauthResponse?.claim_typenme ?? "",
    },
    {
      id: 6,
      title: "Preauthorization Sub Type",
      value: preauthResponse?.preauth_subtype ?? "",
    },

    // Another Advanced row
    ...(isAdvanced
      ? [
        {
          id: 23,
          title: "Advanced Reason",
          value: "",
        },
      ]
      : []),

    {
      id: 7,
      title: "Preauthorization ID",
      value: preauthResponse?.preauth_id ?? "",
    },
    {
      id: 8,
      title: "Related",
      value:
        relatedDisplay || relatedID
          ? `${relatedDisplay || ""} - (${relatedID || ""})`.trim()
          : "",
    },
    {
      id: 9,
      title: "Pre-Authorization Status",
      value: statusValue ? (
        <span
          className="d-inline-block rounded-pill px-2 py-1"
          style={{
            backgroundColor: statusStyle.backgroundColor,
            color: statusStyle.color,
            fontSize: "0.75rem",
            fontWeight: 500,
            border: "1px solid transparent",
            borderColor: "rgba(0,0,0,0.1)",
          }}
        >
          {statusValue}
        </span>
      ) : (
        ""
      ),
    },
    {
      id: 10,
      title: "Bundle Request ID",
      value: preauthResponse?.bundlereq_id ?? "",
    },
    {
      // Prescription row – intentionally empty → will be hidden
      title: "Prescription",
      value: "",
    },
    {
      id: 11,
      title: "Eligibility Reference",
      value: preauthResponse?.eligibility_reference,
    },
    {
      id: 12,
      title: "PreAuth From",
      value: preauthResponse?.preauthfrom ?? "",
    },
  ]
    // Filter out items that have no visible value
    .filter((item) => {
      // For JSX elements we consider the element itself as truthy
      if (
        item.value === "" ||
        item.value === null ||
        item.value === undefined
      ) {
        return false;
      }
      return true;
    });

  const patientDetails = [
    {
      id: 1,
      title: "Identity Type",
      value: advancedPatient?.identity_type || "",
    },
    {
      id: 2,
      title: "Identity No",
      value: advancedPatient?.nationalid || "",
    },
    {
      id: 3,
      title: "Membership No",
      value: advancedPatient?.memberid || "",
    },
    { id: 4, title: "Policy No", value: advancedPatient?.polycyno || "" },
    {
      id: 5,
      title: "Medical Record Number",
      value: advancedPatient?.mrn || "",
    },
    { id: 6, title: "Class Name", value: advancedPatient?.class || "" },
    { id: 7, title: "Gender", value: advancedPatient?.gender || "" },
    {
      id: 8,
      title: "Relation",
      value: "",
    },
    {
      id: 9,
      title: "Martial Status",
      value: advancedPatient?.maritalStatus || "",
    },
    {
      id: 10,
      title: "Occupation",
      value: advancedPatient?.occupation || "",
    },
    {
      id: 11,
      title: "Religion",
      value: advancedPatient?.religion || "",
    },
  ];

  const { age } = ageCalculator(advancedPatient?.birthdate);

  return (
    <>
      <motion.div
        style={{ border: "2px solid #d9f8eb" }}
        className="rounded p-3 position-relative mt-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 50 }}
        transition={{ duration: 0.5 }}
      >
        <div className="position-absolute top-0 start-40 translate-middle-y px-2">
          <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
            <span className="fs-11">Payer response</span>
          </span>
        </div>

        <Row className="mt-1">
          {itemDetails?.map((item) => (
            <Col xs={6} md={4} xl={3} className="mb-3" key={item.id}>
              <Item title={item?.title} value={item?.value} />
            </Col>
          ))}

          {preauthResponse?.preauthto && (
            <Col xs={6} md={4} xl={3} className="mb-3">
              <Item title={"PreAuth To"} value={preauthResponse?.preauthto} />
            </Col>
          )}
          <Col xs={12} className="mb-3">
            <Item
              remark={true}
              title={"Remark"}
              value={
                <div className="d-flex flex-column gap-1">
                  {preauthResponse?.disposition && (
                    <div className="fw-medium">
                      <span className="fw-medium me-2">
                        {"Disposition"}
                      </span>{" "}
                      {preauthResponse.disposition}
                    </div>
                  )}
                  {preauthResponse?.remarks &&
                    preauthResponse?.remarks?.map((item, index) => (
                      <div key={index} className="fw-medium">
                        <span className="fw-medium me-2">
                          {"(Adjustication)"}
                        </span>{" "}
                        {preauthResponse.preautherr_code
                          ? `[${item?.preautherr_code}] - ${item?.preautherr_display}`
                          : item?.preautherr_display}
                      </div>
                    ))}

                  {viewPreauthorizationInfo?.service_procedures?.map(
                    (proc, pIdx) => (
                      <React.Fragment key={pIdx}>
                        {proc?.adjreason?.map((adj, idx) => (
                          <div className="fw-medium" key={idx}>
                            <span className="fw-medium me-2">{"(Adjudication)"}</span>{" "}
                            {adj.code ? `[${adj.code}] - ${adj.name}` : adj.name}
                          </div>
                        ))}
                        {(proc?.reasoncode || proc?.reasondesc) && (
                          <div className="fw-medium">
                            <span className="fw-medium me-2">
                              {"(Adjustication)"}
                            </span>{" "}
                            {proc.reasoncode ? `[${proc.reasoncode}] ` : ""}
                            {proc.reasondesc}
                          </div>
                        )}
                        {proc?.process_note?.map((note, nIdx) => (
                          <div key={nIdx} className="fw-medium">
                            <span className="fw-medium me-2">
                              {"(Process Note)"}
                            </span>{" "}
                            {note.text}
                          </div>
                        ))}
                      </React.Fragment>
                    )
                  )}
                </div>
              }
            />
          </Col>
        </Row>
      </motion.div>
      {viewPreauthorizationInfo?.preauthadv_id && (
        <motion.div
          style={{ border: "2px solid #d9f8eb" }}
          className="rounded p-3 position-relative mt-3 mb-3"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          transition={{ duration: 0.5 }}
        >
          <div className="position-absolute top-0 start-40 translate-middle-y px-2 d-none d-md-block">
            <span className="badge shadow-sm d-flex align-items-center justify-content-center gap-2 badge-subtle-success">
              <span className="fs-11">Beneficiary Details</span>
            </span>
          </div>
          <Row>
            <Col xs={12}>
              <Row>
                <Col xs={4} md={1}>
                  <div className="position-relative d-inline-block">
                    <img
                      src="/test-img.png"
                      alt="Patient"
                      className="img-fluid rounded-circle border"
                      style={{
                        width: "40px",
                        height: "40px",
                        objectFit: "cover",
                        border: "3px solid #2c7be5",
                      }}
                    />
                  </div>
                </Col>
                <Col xs={8} md={11}>
                  <div className="d-flex flex-column">
                    <span className="fs-10 fw-bold ">
                      {advancedPatient?.name || ""}
                    </span>
                    <span className="fs-10 text-600 fw-bold text-capitalize ">
                      {age?.years} Years - {advancedPatient?.gender}
                    </span>
                  </div>
                </Col>
              </Row>
              <hr />
              <Row className="mt-1">
                {patientDetails?.map((item) => (
                  <Col xs={6} md={4} xl={3} className="mb-3" key={item.id}>
                    <Item title={item?.title} value={item?.value} />
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </motion.div>
      )}
    </>
  );
};

export default PreauthPayerResponse;

export const Item = ({ title, value, remark, advanced = false }) => {
  return (
    <div className="d-flex flex-column gap-1 w-100">
      <div className="fs-10 fw-bold">{title}</div>
      <div
        className={`${remark ? "text-danger" : "text-600"
          } fw-normal text-capitalize ${advanced ? "fs-9" : "fs-10"}`}
      >
        {value}
      </div>
    </div>
  );
};

const relation = [
  {
    rltdrlshp_code: "prior",
    rltdrlshp_name: "Prior Claim",
  },
  {
    rltdrlshp_code: "associated",
    rltdrlshp_name: "Associated Claim",
  },
  {
    rltdrlshp_code: "appealing",
    rltdrlshp_name: "Appealing",
  },
  {
    rltdrlshp_code: "extend",
    rltdrlshp_name: "Authorization to extend",
  },
];

const subPreauthType = [
  {
    claimsub_types_id: 1,
    claimsub_types_code: "ip",
    claimsub_types_name: "InPatient",
    claimsub_types_dflt: 0,
  },
  {
    claimsub_types_id: 2,
    claimsub_types_code: "op",
    claimsub_types_name: "OutPatient",
    claimsub_types_dflt: 1,
  },
  {
    claimsub_types_id: 3,
    claimsub_types_code: "emr",
    claimsub_types_name: "Emergency",
    claimsub_types_dflt: 0,
  },
];

const maritalStatusMap = {
  M: "Married",
  S: "Single",
  D: "Divorced",
  W: "Widowed",
  L: "Legally Separated",
  U: "Unmarried",
};

const identityType = {
  PRC: "Iqama",
  PPN: "Passport No.",
  NI: "National ID",
  VP: "Visa No.",
  MR: "Medical record Number ",
  BN: "Border Number",
  DP: "Displaced person",
};

const religion = [
  {
    religion_code: "0",
    religion_name: "Other",
  },
  {
    religion_code: "1",
    religion_name: "Muslim",
  },
  {
    religion_code: "2",
    religion_name: "Christian",
  },
  {
    religion_code: "3",
    religion_name: "Judaism",
  },
  {
    religion_code: "4",
    religion_name: "Buddhism",
  },
  {
    religion_code: "5",
    religion_name: "Zoroastrian",
  },
  {
    religion_code: "7",
    religion_name: "Hinduism",
  },
  {
    religion_code: "8",
    religion_name: "Sikh",
  },
  {
    religion_code: "9",
    religion_name: "Without",
  },
  {
    religion_code: "98",
    religion_name: "Not available",
  },
  {
    religion_code: "99",
    religion_name: "Not Mentioned",
  },
];

const occupation = [
  {
    occup_code: "administration",
    occup_disply: "Administration",
  },
  {
    occup_code: "agriculture",
    occup_disply: "Agriculture",
  },
  {
    occup_code: "business",
    occup_disply: "Business",
  },
  {
    occup_code: "education",
    occup_disply: "Education",
  },
  {
    occup_code: "housewife",
    occup_disply: "Housewife",
  },
  {
    occup_code: "marine",
    occup_disply: "Marine",
  },
  {
    occup_code: "medical field",
    occup_disply: "Medical Field",
  },
  {
    occup_code: "military",
    occup_disply: "Military",
  },
  {
    occup_code: "skilled worker",
    occup_disply: "Skilled Worker",
  },
  {
    occup_code: "student",
    occup_disply: "Student",
  },
  {
    occup_code: "oil industries",
    occup_disply: "Oil Industries",
  },
  {
    occup_code: "unemployed",
    occup_disply: "Unemployed",
  },
  {
    occup_code: "others",
    occup_disply: "Others",
  },
  {
    occup_code: "unknown",
    occup_disply: "Unknown",
  },
];
